import React, { useEffect, useLayoutEffect, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import Chart from "../../../../Components/Chart/Chart";
import useChart from "../../../../Components/Chart/useChart";
import { createDateAxis, createValueAxis } from "../../Trends/TrendsUtils";
import useTrend from "../../Trends/useTrend";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../../../General/useSeverityAsPriority";
import { capitalize } from "lodash-es";
import type { SelectedScope } from "../../../../types/scope";

export interface OverdueFindingsBySeverityProps {
  selectedScope?: SelectedScope | null;
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  timeScale: string;
  id: string | number;
  widgetKey?: string;
  setIsReadyToExport?: (isReady: boolean) => void;
}

// A single point on a severity series, as accumulated from the raw trend rows.
interface SeriesPoint {
  date: string;
  value: number;
  dashLength?: number;
}

// dataObj keyed by severity value -> list of points.
type SeriesDataMap = Record<string, SeriesPoint[]>;

// Raw trend rows: [date, severity, value] tuples returned by useTrend.
type TrendRow = [string, string, number];

const OverdueFindingsBySeverity: React.FC<OverdueFindingsBySeverityProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  timeScale,
  id,
  widgetKey,
  setIsReadyToExport,
}) => {
  const { getSeverityField } = useSeverityAsPriority();
  const {
    trendData: data,
    trendDataError,
    trendLoading,
    refetchTrend,
  } = useTrend({
    variables: {
      ...(widgetKey ? { widgetKey } : {}),
      timescale: timeScale,
      selection: ["broken_sla"],
      group_by: ["severity"],
      aggregate_functions: ["SUM"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute,
        scopesid: selectedScope?.value?.id,
        filters_fields_to_exclude: ["remediation_item.actual_status"],
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });
  const [trendData, setTrendData] = useState<SeriesDataMap | null>(null);
  const chartName = `OverdueFindingsBySeverity-${id}`;

  const {
    chartLoading,
    dataError,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart(setIsReadyToExport ?? null);

  const clearData = () => {
    setTrendData(null);
  };

  useEffect(() => {
    if (!!trendLoading) {
      startLoadingChart();
      clearData();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [trendLoading, setChartDataExists, setChartError, startLoadingChart]);

  useEffect(() => {
    if (!!trendDataError) {
      setChartError(true);
      stopLoadingChart();
    }
  }, [trendDataError, setChartError, stopLoadingChart]);

  useEffect(() => {
    if (!!data) {
      // `data` is typed as unknown by useTrend; this widget consumes it as an
      // array of [date, severity, value] tuples.
      const rows = data as TrendRow[];
      const dataObj: SeriesDataMap = {};

      for (let i = 0; i < rows.length; i += 1) {
        const severity = rows[i][1];
        if (!dataObj.hasOwnProperty(severity)) {
          dataObj[severity] = [];
        }

        dataObj[severity].push({
          date: rows[i][0],
          value: rows[i][2],
        });
      }

      if (
        !dataObj ||
        !Object.values(dataObj).find((obj) =>
          obj.find((item) => item.value !== 0)
        )
      ) {
        stopLoadingChart();
        setChartDataExists(false);
      } else if (Object.keys(dataObj)?.length) {
        setChartError(false);
        stopLoadingChart();
        setChartDataExists(true);
      }

      Object.keys(dataObj).forEach((item) => {
        const itemData = dataObj[item];
        if (itemData?.length >= 2) {
          itemData[itemData?.length - 2].dashLength = 8;
        }
      });

      setTrendData(dataObj);
    }
  }, [data]);

  useLayoutEffect(() => {
    if (dataExists) {
      stopLoadingChart();

      let [chart, legendContainer] = createChart(chartName, am4charts.XYChart);

      chart.cursor = new am4charts.XYCursor();
      chart.paddingTop = 45;
      chart.paddingRight = 30;
      chart.paddingLeft = 20;
      chart.paddingBottom = 0;

      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      createDateAxis(dateAxis, timeScale);
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      createValueAxis(valueAxis, "Open Findings %");
      valueAxis.calculateTotals = true;
      valueAxis.renderer.labels.template.adapter.add("text", function (text) {
        return text + "%";
      });
      valueAxis.min = 0;
      valueAxis.max = 100;
      valueAxis.paddingLeft = 0;
      valueAxis.paddingRight = 0;
      valueAxis.renderer.labels.template.dy = 0;
      valueAxis.renderer.labels.template.dx = 0;
      valueAxis.title.dx = 10;
      valueAxis.getSeriesDataItem = function (
        this: am4charts.ValueAxis,
        series,
        position
      ) {
        const key = this.axisFieldName + this.axisLetter;
        const value = this.positionToValue(position);
        const dataItem = series.dataItems.getIndex(
          series.dataItems.findClosestIndex(
            value,
            ((x: unknown) => {
              const record = x as unknown as Record<string, number | undefined>;
              return record[key] ? record[key]! : undefined;
            }) as (value: unknown) => number,
            "any"
          )
        );
        return dataItem;
      } as typeof valueAxis.getSeriesDataItem;

      let label = dateAxis.renderer.labels.template;
      label.wrap = true;
      label.maxWidth = 85;
      label.textAlign = "middle";

      if (trendData) {
        for (const [key] of Object.entries(trendData).sort(
          (a, b) => Number(b[0]) - Number(a[0])
        )) {
          createSeries(
            key,
            capitalize(getSeverityField(key, SeverityFieldsEnum.NAME))
          );
        }
      }

      function createSeries(s: string, name: string, legendPosition?: number) {
        let series = chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = "value";
        series.dataFields.valueYShow = "totalPercent";
        series.dataFields.dateX = "date";
        series.name = name;
        series.tensionX = 0.8;
        series.strokeWidth = 2;
        series.stroke = am4core.color(
          getSeverityField(s, SeverityFieldsEnum.COLOR)
        );
        series.fill = am4core.color(
          getSeverityField(s, SeverityFieldsEnum.COLOR)
        );
        series.tooltipText =
          "{name}: {valueY.totalPercent.formatNumber('#.#')}% ({value})";
        series.tooltip!.autoTextColor = false;
        (series as am4charts.LineSeries & { order?: number }).order =
          legendPosition as number;

        const bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.strokeWidth = 2;
        bullet.circle.radius = 3;

        let segment = series.segments.template;
        segment.interactionsEnabled = true;

        segment.events.on("over", function (event) {
          // segment -> segments container -> series; typed as Container at the
          // boundary but is the owning LineSeries at runtime.
          processOver(
            event.target.parent!.parent!
              .parent as unknown as am4charts.LineSeries
          );
        });

        segment.events.on("out", function (event) {
          processOut(
            event.target.parent!.parent!
              .parent as unknown as am4charts.LineSeries
          );
        });

        series.data = trendData![s];
        series.strokeWidth = 2;
        series.propertyFields.strokeDasharray = "dashLength";

        return series;
      }

      chart.legend.events.on("datavalidated", function (ev) {
        ev.target.itemContainers.each(function (item) {
          // dataContext is typed as `{}` by amCharts; at runtime it carries the
          // series object whose `order` we read.
          item.zIndex = (item.dataItem!.dataContext as { order: number }).order;
          ev.target.children.moveValue(item, item.zIndex);
        });
        ev.target.hidden = false;
      });

      chart.legend.itemContainers.template.events.on("over", function (event) {
        processOver(event.target.dataItem!.dataContext as am4charts.LineSeries);
      });

      chart.legend.itemContainers.template.events.on("out", function (event) {
        processOut(event.target.dataItem!.dataContext as am4charts.LineSeries);
      });

      const processOver = (hoveredSeries: am4charts.LineSeries) => {
        hoveredSeries.toFront();

        hoveredSeries.segments.each(function (segment) {
          segment.setState("hover");
        });

        chart.series.each(function (series) {
          if (series !== hoveredSeries) {
            (series as am4charts.LineSeries).segments.each(function (segment) {
              segment.setState("dimmed");
            });
            series.bulletsContainer.setState("dimmed");
          }
        });
      };

      function processOut(hoveredSeries: am4charts.LineSeries) {
        chart.series.each(function (series) {
          (series as am4charts.LineSeries).segments.each(function (segment) {
            segment.setState("default");
          });
          series.bulletsContainer.setState("default");
        });
      }

      return () => {
        chart.dispose();
        legendContainer?.dispose();
      };
    }
  }, [trendData, dataExists, createChart]);

  return (
    <Chart
      chartLoading={chartLoading}
      chartName={chartName}
      chartError={dataError}
      chartData={dataExists}
      chartReload={refetchTrend}
    />
  );
};

export default OverdueFindingsBySeverity;
